import { Drawer } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { isEmpty } from 'lodash-es';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { ButtonV2 } from '~/src/dash/components/buttons/ButtonV2';
import {
  LoadingState,
  LoadingStateNoData,
} from '~/src/dash/components/dashboard/dashboard-graph-v2';
import { TextInput } from '~/src/dash/components/input/TextInput';
import { Col, Cols, Row, Rows } from '~/src/dash/components/layout';
import { spacing } from '~/src/dash/components/layout/common';
import { Div } from '~/src/dash/components/layout/Div';
import { LiveLogsButton } from '~/src/dash/components/live_logs/live-logs-button';
import {
  ILiveLogsParsedResponses,
  ILiveLogsResponse,
  ILiveLogsTab,
} from '~/src/dash/components/LiveLogs/common';
import { LiveLogsTable } from '~/src/dash/components/LiveLogs/Table';
import { SimpleTabs } from '~/src/dash/components/tabs/SimpleTabs';
import { Text } from '~/src/dash/components/text';
import { publicaColors } from '~/src/dash/utils/colors';
import { formatTime } from '~/src/dash/utils/date';
import { LoadingStateHasData, LoadingStateLoading } from '~/src/dash/utils/loading-states';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

interface ILiveLogs {
  getLiveLogs: () => Promise<any>; // async function to fetch live logs on load and on refresh
  // tell the component how to format the data that we get back from getLiveLogs
  formatLiveLogs: (responses: Array<ILiveLogsResponse>) => ILiveLogsParsedResponses;
}

export const LiveLogs = ({ getLiveLogs, formatLiveLogs }: ILiveLogs) => {
  const { user } = useBaseStore();
  const [isModalOpen, setModalOpen] = useState(false);
  const [liveLogs, setLiveLogs] = useState<ILiveLogsParsedResponses>([]);
  const [liveLogsTimestamp, setLiveLogsTimestamp] = useState('');
  const [error, setError] = useState<any>(null);
  const [loadState, setLoadState] = useState<LoadingState>(LoadingStateNoData);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  // selected tab & filter
  const [activeTabIdx, setActiveTabIdx] = useState(0);
  const [searchInput, setSearchInput] = useState('');

  // error handler
  const onError = (err: any, message: string = '') => {
    unstable_batchedUpdates(() => {
      setError(err);
      setSnackbarMessage(message);
    });
  };

  // clear error handler
  const onClearError = () => {
    unstable_batchedUpdates(() => {
      setError(null);
      setSnackbarMessage('');
    });
  };

  // switch between tab handler
  const onChangeTab = (_event: React.SyntheticEvent, index: number) => {
    unstable_batchedUpdates(() => {
      setActiveTabIdx(index);
      setSearchInput('');
    });
  };

  // helper to filter liveLogs based on the selected tab and filter
  const getTableData = (): ILiveLogsTab | null => {
    if (
      isEmpty(liveLogs) ||
      isEmpty(liveLogs[activeTabIdx]) ||
      isEmpty(liveLogs[activeTabIdx].data)
    )
      return null;
    return {
      ...liveLogs[activeTabIdx],
      data: liveLogs[activeTabIdx].data
        .filter((liveLog: Record<string, any>) =>
          JSON.stringify(liveLog).toLowerCase().includes(searchInput.toLowerCase()),
        )
        .sort((a, b): number => {
          return moment(b.timestamp).unix() - moment(a.timestamp).unix();
        }),
    };
  };

  // fetch and parse liveLogs
  const fetchLiveLogs = async () => {
    setLoadState(LoadingStateLoading);
    const responses = await getLiveLogs().catch((err) => {
      onError(err, 'Error retrieving campaign live logs.');
      return;
    });
    unstable_batchedUpdates(() => {
      setLiveLogs(formatLiveLogs(responses));
      setLiveLogsTimestamp(formatTime(moment(), user.publisher.Timezone));
      setLoadState(LoadingStateHasData);
    });
  };

  // refetch liveLogs whenever the modal open
  useEffect(() => {
    if (isModalOpen) fetchLiveLogs();
  }, [isModalOpen]);

  return (
    <>
      <LiveLogsButton onClick={() => setModalOpen(true)} />

      <Drawer anchor='right' open={isModalOpen} onClose={() => setModalOpen(false)}>
        <Rows style={{ width: '80vw', overflow: 'hidden' }}>
          <Row largePadding paddingBottom='0' style={{ position: 'sticky' }}>
            <Cols paddingBottom={spacing.base} style={{ alignItems: 'center' }}>
              <Col>
                <Text header bold>
                  Live Logs
                </Text>
                {liveLogsTimestamp && (
                  <Text small muted>{`Last updated at ${formatTime(
                    moment(),
                    user.publisher.Timezone,
                  )}`}</Text>
                )}
              </Col>
              <Col marginLeft='auto'>
                <ButtonV2
                  allCaps
                  backgroundColor={publicaColors.blue}
                  color={publicaColors.white}
                  onClick={fetchLiveLogs}
                  disabled={loadState === LoadingStateLoading}
                >
                  Refresh
                </ButtonV2>
              </Col>
            </Cols>

            <Div style={{ paddingBottom: spacing.small }}>
              <TextInput
                enableClear
                handleOnChange={(value) => setSearchInput(value)}
                msDelay={800}
                placeholder='Search by keyword or value'
                prefix='search'
                initialValue={searchInput}
              />
            </Div>

            {!isEmpty(liveLogs) && (
              <SimpleTabs
                activeTabIndex={activeTabIdx}
                ariaLabel='Live Logs'
                handleChangeTab={onChangeTab}
                tabs={liveLogs.map((log, idx) => ({
                  value: idx,
                  key: log.key,
                  label: log.label,
                }))}
              />
            )}
          </Row>

          <Row
            largeHorizontalPadding
            paddingTop={spacing.base}
            style={{ overflow: 'scroll', position: 'relative' }}
          >
            {loadState === LoadingStateLoading ? (
              <Skeleton variant='rect' animation='wave' width='100%'>
                <LiveLogsTable liveLogs={getTableData()} />
              </Skeleton>
            ) : (
              <LiveLogsTable liveLogs={getTableData()} />
            )}
          </Row>
        </Rows>

        <SnackbarView open={!!snackbarMessage} error={error} onClose={onClearError}>
          {snackbarMessage}
        </SnackbarView>
      </Drawer>
    </>
  );
};
